import { Link } from "react-router-dom"
import {
  Calendar,
  Phone,
  MapPin,
  ChevronRight,
  Stethoscope,
  Baby,
  FlaskConical,
  Scissors,
  Syringe,
  Heart,
  CheckCircle,
  ArrowRight,
  Sparkles,
  Clock,
  Shield,
  ChevronDown,
} from "lucide-react"
import SEO from "./SEO"

import { Button } from "../components/ui/button"
import { Card, CardContent, CardFooter } from "../components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs"
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "../components/ui/carousel"
import { useEffect, useState } from "react"
import { Badge } from "../components/ui/badge"

export default function MaternityMatters() {
  const maternitySchema = [
    {
      "@context": "https://schema.org",
      "@type": "MedicalOrganization",
      "name": "Pranaam Hospitals",
      "url": "https://pranaam.pranaamhospitals.in/",
      "logo": "https://pranaam.pranaamhospitals.in/wp-content/uploads/2025/logo.png",
      "description": "Pranaam Hospitals is a NABH-accredited multi-specialty hospital in Hyderabad providing comprehensive maternity, pregnancy, and childbirth care.",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Hyderabad",
        "addressRegion": "Telangana",
        "addressCountry": "IN"
      },
      "contactPoint": [
        {
          "@type": "ContactPoint",
          "contactType": "appointments",
          "telephone": "+91-99660-60000",
          "availableLanguage": ["English", "Telugu", "Hindi"]
        },
        {
          "@type": "ContactPoint",
          "contactType": "emergency",
          "telephone": "+91-98485-98285",
          "availableLanguage": ["English", "Telugu", "Hindi"]
        }
      ]
    },

    {
      "@context": "https://schema.org",
      "@type": "Article",
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": "https://pranaam.pranaamhospitals.in/maternity-matters"
      },
      "headline": "Maternity Matters – Pregnancy & Childbirth Care at Pranaam Hospitals",
      "description": "Maternity Matters at Pranaam Hospitals provides personalized prenatal care, safe deliveries, high-risk pregnancy management, and postnatal support in Hyderabad.",
      "author": {
        "@type": "Organization",
        "name": "Pranaam Hospitals"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Pranaam Hospitals",
        "logo": {
          "@type": "ImageObject",
          "url": "https://pranaam.pranaamhospitals.in/wp-content/uploads/2025/logo.png"
        }
      },
      "datePublished": "2015-01-01",
      "dateModified": "2025-04-01",
      "keywords": [
        "maternity care Hyderabad",
        "pregnancy hospital Hyderabad",
        "best gynecology hospital Hyderabad",
        "normal delivery hospital Hyderabad",
        "high risk pregnancy care Hyderabad"
      ]
    }
  ];

  const galleryCarouselImages = [
    "/images/portrait-beautiful-pregnant-woman-1.jpg",
    "/images/pregnant-woman.jpg",
    "/images/portrait-newborn-baby-with-mother.jpg",
    "/images/portrait-beautiful-pregnant-woman.jpg",
  ]

  const [pageData, setPageData] = useState(null)

  useEffect(() => {
    fetch("https://pranaam.pranaamhospitals.in/wp-json/wp/v2/pages?slug=maternity-matters")
      .then((res) => res.json())
      .then((data) => setPageData(data[0]))
  }, [])

  if (!pageData) return null

  const acf = pageData.acf

  return (
    <>
      <SEO
        title="Maternity Matters | Safe Pregnancy & Childbirth Care at Pranaam Hospitals"
        description="Maternity Matters at Pranaam Hospitals offers expert pregnancy care, safe delivery, prenatal & postnatal services with experienced gynecologists in Hyderabad."
        canonical="https://pranaam.pranaamhospitals.in/maternity-matters"
        robots="index, follow"
        author="Pranaam Hospitals"
        publisher="Pranaam Hospitals"
        keywords="Maternity Matters Pranaam Hospitals, maternity hospital Hyderabad,
            pregnancy care Hyderabad, best gynecologist hospital Hyderabad,
            normal delivery hospital Hyderabad, high risk pregnancy care,
            prenatal care Hyderabad, postnatal care Hyderabad"
        schema={maternitySchema}
      />



      <div className="min-h-screen bg-white">
        <main>

          {/* Hero Section */}
          <section className="relative h-[80vh] min-h-[500px] flex items-center">

            <div className="absolute inset-0 z-0">
              <img
                src={acf.hero_bg_image}
                alt="Maternity Care"
                className="w-full h-full object-cover brightness-50"
              />
            </div>

            <div className="container relative z-10 px-4 mx-auto text-white">
              <div className="max-w-2xl">

                <Badge className="mb-4 bg-white/20 text-white border-white/30">
                  <Sparkles className="w-4 h-4 mr-2" />
                  {acf.hero_badge}
                </Badge>

                <h1 className="mt-4 text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl">
                  {acf.hero_title_line_1}{" "}
                  <span className="text-primary">
                    {acf.hero_title_highlight}
                  </span>
                </h1>

                <p className="max-w-md mt-6 text-lg text-white/90">
                  {acf.hero_description}
                </p>

                <div className="flex flex-wrap gap-4 mt-8">
                  <Button
                    size="lg"
                    className="gap-2 group transition-all duration-300 hover:translate-y-[-2px] shadow-lg bg-white text-primary hover:bg-white/90"
                  >
                    <Calendar className="w-5 h-5 group-hover:scale-110 transition-transform" />

                    <a
                      href={acf.hero_button_link}
                      rel="noopener noreferrer"
                    >
                      {acf.hero_button_text}
                    </a>
                  </Button>
                </div>

              </div>
            </div>

            <div className="absolute bottom-0 left-0 right-0 flex justify-center">
              <a
                href={acf.hero_scroll_link}
                className="flex items-center justify-center w-10 h-10 mb-8 text-white bg-primary/50 rounded-full backdrop-blur-sm animate-bounce"
              >
                <ChevronDown className="w-6 h-6" />
                <span className="sr-only">Scroll Down</span>
              </a>
            </div>

          </section>

          {/* Maternity Matters Highlight Section */}
          <section id="maternity-matters" className="py-16 bg-white">

            <div className="container px-4 mx-auto">

              <div className="flex flex-col items-center mb-10 text-center">

                <Badge className="mb-4 bg-primary/10 text-primary">
                  <Baby className="w-5 h-5 mr-2" />
                  {acf.highlight_badge}
                </Badge>

                <h2 className="mb-4 text-3xl font-bold md:text-4xl lg:text-5xl">
                  {acf.highlight_title_line1}{" "}
                  <span className="text-primary">
                    {acf.highlight_title_highlight}
                  </span>
                </h2>

                <p className="mb-6 text-xl italic text-muted-foreground">
                  {acf.highlight_subtitle}
                </p>

                <p className="max-w-3xl text-lg text-muted-foreground">
                  {acf.highlight_description}
                </p>

              </div>

              {/* Gallery */}

              <div className="mt-12 mb-16">

                <Carousel className="w-full">

                  <CarouselContent>

                    {acf.gallery_images?.map((item, index) => (

                      <CarouselItem
                        key={index}
                        className="basis-full sm:basis-1/2 md:basis-1/3 lg:basis-1/3"
                      >

                        <div className="p-1">

                          <div className="overflow-hidden rounded-xl">

                            <img
                              src={item.image}
                              alt="Maternity Gallery"
                              className="w-full h-[20rem] object-cover transition-transform duration-500 hover:scale-105"
                            />

                          </div>

                        </div>

                      </CarouselItem>

                    ))}

                  </CarouselContent>

                  <div className="flex justify-center mt-4">
                    <CarouselPrevious className="relative mr-2 left-0" />
                    <CarouselNext className="relative ml-2 right-0" />
                  </div>

                </Carousel>

              </div>

              {/* Feature Cards */}

              <div className="grid grid-cols-1 gap-6 mt-12 sm:grid-cols-3">

                {acf.feature_cards?.map((card, index) => (

                  <Card
                    key={index}
                    className="overflow-hidden transition-all border-none shadow-lg hover:shadow-xl hover:translate-y-[-5px] duration-300"
                  >

                    <div className="relative h-80">

                      <img
                        src={card.image}
                        alt={card.title}
                        className="w-full h-full object-cover"
                      />

                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>

                      <div className="absolute bottom-0 left-0 right-0 p-4">

                        <h3 className="text-lg font-bold text-white">
                          {card.title}
                        </h3>

                      </div>

                    </div>

                    <CardContent className="p-6">

                      <p className="text-muted-foreground">
                        {card.description}
                      </p>

                    </CardContent>

                  </Card>

                ))}

              </div>

              {/* Highlights List */}

              <div className="p-6 mt-12 rounded-2xl bg-gray-100">

                <h3 className="mb-4 text-xl font-bold">
                  Highlights of Maternity Matters:
                </h3>

                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">

                  {acf.maternity_highlights?.map((item, index) => (

                    <div key={index} className="flex items-start gap-2">

                      <CheckCircle className="w-5 h-5 mt-0.5 text-primary shrink-0" />

                      <span>{item.text}</span>

                    </div>

                  ))}

                </div>

                <p className="mt-6 text-lg font-medium text-center">

                  Whether it's your first baby or your third, our team ensures you feel empowered, informed, and cared for every day.

                </p>

              </div>

              {/* CTA */}

              <div className="flex justify-center mt-10">

                <Button size="lg" className="gap-2 shadow-md" asChild>

                  <a
                    href={acf.cta_button_link}
                    rel="noopener noreferrer"
                  >

                    {acf.cta_button_text}

                    <ArrowRight className="w-4 h-4 ml-1" />

                  </a>

                </Button>

              </div>

            </div>

          </section>

          {/* Visual Journey Section */}
          <section className="py-16 bg-gray-100">
          <div className="container px-4 mx-auto">

          <div className="flex flex-col items-center mb-12 text-center">

          <Badge className="mb-4 bg-white text-primary">
          <Clock className="w-5 h-5 mr-2" />
          {acf.journey_badge}
          </Badge>

          <h2 className="mb-4 text-3xl font-bold md:text-4xl lg:text-5xl">
          {acf.journey_title_part1}{" "}
          <span className="text-primary">
          {acf.journey_highlight1}
          </span>{" "}
          {acf.journey_title_part2}{" "}
          <span className="text-primary">
          {acf.journey_highlight2}
          </span>
          </h2>

          <p className="max-w-2xl text-lg text-muted-foreground">
          {acf.journey_description}
          </p>

          </div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">

          {acf.journey_phases?.map((phase,index)=>(

          <div key={index} className="flex flex-col h-full">

          <div className="relative h-64 mb-4 overflow-hidden rounded-xl">

          <img
          src={phase.image}
          alt={phase.title}
          className="w-full h-full object-cover"
          />

          </div>

          <h3 className="mb-2 text-xl font-bold">
          {phase.title}
          </h3>

          <p className="mb-4 text-muted-foreground">
          {phase.description}
          </p>

          <ul className="pl-5 mt-auto space-y-1 list-disc">

          {phase.items?.map((item,idx)=>(
          <li key={idx} className="text-sm">
          {item.item_text}
          </li>
          ))}

          </ul>

          </div>

          ))}

          </div>

          </div>
          </section>

          {/* Services Section with Visual Tabs */}
         <section className="py-16 bg-white">
          <div className="container px-4 mx-auto">

          <div className="flex flex-col items-center mb-12 text-center">

          <Badge className="mb-4 bg-gray-100 text-primary">
          <Stethoscope className="w-5 h-5 mr-2" />
          {acf.services_badge}
          </Badge>

          <h2 className="mb-4 text-3xl font-bold md:text-4xl lg:text-5xl">
          {acf.services_title1}{" "}
          <span className="text-primary">
          {acf.services_title_highlight}
          </span>
          </h2>

          <p className="max-w-2xl text-lg text-muted-foreground">
          {acf.services_description}
          </p>

          </div>

          <Tabs
            defaultValue={acf.services_tabs?.[0]?.tab_slug?.toLowerCase()}
            className="w-full max-w-5xl mx-auto"
          >

          <TabsList className="grid w-full grid-cols-2 mb-8">

          {acf.services_tabs?.map((tab,index)=>(
            <TabsTrigger
              key={index}
              value={tab.tab_slug.toLowerCase()}
            >
              {tab.tab_name}
            </TabsTrigger>
          ))}

          </TabsList>

          {acf.services_tabs?.map((tab,index)=>(
          <TabsContent
            key={index}
            value={tab.tab_slug.toLowerCase()}
            className="overflow-hidden rounded-2xl shadow-md"
          >

          <div className="grid grid-cols-1 md:grid-cols-2">

          <div className="relative h-full min-h-[300px]">

          <img
          src={tab.image}
          alt={tab.title}
          className="w-full h-full object-cover"
          />

          </div>

          <div className="p-8 bg-white">

          <div className="flex items-center gap-3 mb-6">

          <div className="p-3 rounded-full bg-gray-100">
          <Stethoscope className="w-8 h-8 text-primary" />
          </div>

          <h3 className="text-2xl font-bold">
          {tab.title}
          </h3>

          </div>

          <p className="mb-6 text-lg">
          {tab.description}
          </p>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">

          {tab.items?.map((item,i)=>(
          <div key={i} className="flex items-start gap-2">

          <CheckCircle className="w-5 h-5 mt-1 text-primary shrink-0"/>

          <span>{item.item_text}</span>

          </div>
          ))}

          </div>

          </div>

          </div>

          </TabsContent>

          ))}

          </Tabs>

          </div>
          </section>

        {/* Facilities Gallery */}
        <section className="py-16 bg-gray-100">

        <div className="container px-4 mx-auto">

        <div className="flex flex-col items-center mb-12 text-center">

        <div className="inline-flex items-center px-4 py-2 mb-4 space-x-2 text-sm font-medium rounded-full bg-white text-brand-magenta">

        <Shield className="w-5 h-5" />

        <span>{acf.facilities_badge}</span>

        </div>

        <h2 className="mb-4 text-3xl font-bold md:text-4xl lg:text-5xl">

        {acf.facilities_title1}{" "}

        <span className="text-brand-magenta">

        {acf.facilities_title_highlight}

        </span>

        </h2>

        <p className="max-w-2xl text-lg text-muted-foreground">

        {acf.facilities_description}

        </p>

        </div>


        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">

        {acf.facilities_list?.map((facility,index)=>(

        <div key={index} className="group">

        <div className="overflow-hidden rounded-xl">

        <div className="relative h-64">

        <img
        src={facility.image}
        alt={facility.title}
        className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-110"
        />

        <div className="absolute inset-0 transition-opacity bg-black/30 group-hover:opacity-0"></div>

        <div className="absolute inset-0 flex items-center justify-center">

        <h3 className="px-4 py-2 text-lg font-bold text-white transition-all duration-300 bg-brand-magenta/80 rounded-md group-hover:bg-brand-magenta">

        {facility.title}

        </h3>

        </div>

        </div>

        </div>

        </div>

        ))}

        </div>

        </div>

        </section>

          {/* About Us Section with Image */}
        <section className="py-16 bg-white">
        <div className="container px-4 mx-auto">

        <div className="grid items-center grid-cols-1 gap-12 md:grid-cols-2">

        {/* Image */}

        <div className="relative">

        <div className="relative h-64 overflow-hidden rounded-2xl shadow-xl md:h-96">

        <img
        src={acf.about_image}
        alt="Women's Healthcare Team"
        className="object-cover w-full h-full"
        />

        </div>

        <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-brand-magenta/10 rounded-full"></div>

        <div className="absolute -top-6 -left-6 w-24 h-24 bg-brand-magenta/10 rounded-full"></div>

        </div>


        {/* Content */}

        <div>

        <div className="inline-flex items-center px-4 py-2 mb-4 space-x-2 text-sm font-medium rounded-full bg-gray-100 text-brand-magenta">

        <Heart className="w-5 h-5" />

        <span>{acf.about_badge}</span>

        </div>


        <h2 className="mb-6 text-3xl font-bold md:text-4xl lg:text-5xl">

        {acf.about_title1}{" "}

        <span className="text-brand-magenta">

        {acf.about_title_highlight}

        </span>{" "}

        {acf.about_title2}

        </h2>


        <p className="mb-8 text-lg text-muted-foreground">

        {acf.about_description}

        </p>


        <div className="flex flex-wrap gap-4">

        <Button
        className="gap-2 shadow-md bg-brand-magenta hover:bg-brand-magenta/90"
        asChild
        >

        <Link to={acf.about_btn1_link}>

        {acf.about_btn1_text}

        <ChevronRight className="w-4 h-4"/>

        </Link>

        </Button>


        <Button
        variant="outline"
        className="gap-2 border-brand-magenta text-brand-magenta hover:bg-brand-magenta hover:text-white"
        asChild
        >

        <Link to={acf.about_btn2_link}>

        {acf.about_btn2_text}

        <ChevronRight className="w-4 h-4"/>

        </Link>

        </Button>

        </div>

        </div>

        </div>

        </div>

        </section>

          {/* Contact Section with Visual Cards */}
          {/* <section className="py-16 bg-gray-100">
          <div className="container px-4 mx-auto">
            <div className="flex flex-col items-center mb-12 text-center">
              <div className="inline-flex items-center px-4 py-2 mb-4 space-x-2 text-sm font-medium rounded-full bg-white text-brand-magenta">
                <Phone className="w-5 h-5" />
                <span>Get in Touch</span>
              </div>
              <h2 className="mb-4 text-3xl font-bold md:text-4xl lg:text-5xl">
                Contact <span className="text-brand-magenta">Us</span>
              </h2>
              <p className="max-w-2xl text-lg text-muted-foreground">
                Have questions or want to schedule an appointment? Reach out to us through any of these channels.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-4">
              <Card className="overflow-hidden transition-all border-none shadow-lg hover:shadow-xl hover:translate-y-[-5px] duration-300">
                <div className="relative h-40">
                  <img
                    src="/placeholder.svg?height=300&width=500&text=Call+Us"
                    alt="Call Us"
                    fill
                    className="object-cover"
                  />
                  <div className="absolute top-[110%] right-[42%]">
                            <h1>324 × 324 px</h1>
                          </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="text-lg font-bold text-white">Call Us</h3>
                  </div>
                </div>
                <CardContent className="p-6 text-center">
                  <p className="text-muted-foreground">1800-123-4567</p>
                </CardContent>
                <CardFooter className="p-4 pt-0 justify-center">
                  <Button variant="ghost" size="sm" className="text-brand-magenta" asChild>
                    <Link href="tel:18001234567">Call Now</Link>
                  </Button>
                </CardFooter>
              </Card>

              <Card className="overflow-hidden transition-all border-none shadow-lg hover:shadow-xl hover:translate-y-[-5px] duration-300">
                <div className="relative h-40">
                  <img
                    src="/placeholder.svg?height=300&width=500&text=Visit+Us"
                    alt="Visit Us"
                    fill
                    className="object-cover"
                  />
                   <div className="absolute top-[110%] right-[42%]">
                            <h1>324 × 324 px</h1>
                          </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="text-lg font-bold text-white">Visit Us</h3>
                  </div>
                </div>
                <CardContent className="p-6 text-center">
                  <p className="text-muted-foreground">123 Healthcare Avenue, Medical District, City - 400001</p>
                </CardContent>
                <CardFooter className="p-4 pt-0 justify-center">
                  <Button variant="ghost" size="sm" className="text-brand-magenta" asChild>
                    <Link href="https://maps.google.com" target="_blank">
                      Get Directions
                    </Link>
                  </Button>
                </CardFooter>
              </Card>

              <Card className="overflow-hidden transition-all border-none shadow-lg hover:shadow-xl hover:translate-y-[-5px] duration-300">
                <div className="relative h-40">
                  <img
                    src="/placeholder.svg?height=300&width=500&text=Email+Us"
                    alt="Email Us"
                    fill
                    className="object-cover"
                  />
                   <div className="absolute top-[110%] right-[42%]">
                            <h1>324 × 324 px</h1>
                          </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="text-lg font-bold text-white">Email Us</h3>
                  </div>
                </div>
                <CardContent className="p-6 text-center">
                  <p className="text-muted-foreground">info@pranaamhospitals.com</p>
                </CardContent>
                <CardFooter className="p-4 pt-0 justify-center">
                  <Button variant="ghost" size="sm" className="text-brand-magenta" asChild>
                    <Link href="mailto:info@pranaamhospitals.com">Send Email</Link>
                  </Button>
                </CardFooter>
              </Card>

              <Card className="overflow-hidden transition-all border-none shadow-lg hover:shadow-xl hover:translate-y-[-5px] duration-300">
                <div className="relative h-40">
                  <img
                    src="/placeholder.svg?height=300&width=500&text=Book+Online"
                    alt="Book Online"
                    fill
                    className="object-cover"
                  />
                   <div className="absolute top-[110%] right-[42%]">
                            <h1>324 × 324 px</h1>
                          </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="text-lg font-bold text-white">Book Online</h3>
                  </div>
                </div>
                <CardContent className="p-6 text-center">
                  <p className="text-muted-foreground">Schedule your appointment online</p>
                </CardContent>
                <CardFooter className="p-4 pt-0 justify-center">
                  <Button variant="ghost" size="sm" className="text-brand-magenta" asChild>
                    <Link to="/book-appointment">Schedule Now</Link>
                  </Button>
                </CardFooter>
              </Card>
            </div>
          </div>
        </section> */}

          {/* CTA Section with Background Image */}
          {/* <section className="relative py-20 text-white">
          <div className="absolute inset-0 z-0">
            <img
              src="/placeholder.svg?height=800&width=1600&text=Ready+To+Begin"
              alt="Ready To Begin"
              className="w-full h-full object-cover brightness-50"
            />
          </div>
          <div className="container relative z-10 px-4 mx-auto">
            <div className="flex flex-col items-center text-center">
              <h2 className="mb-6 text-3xl font-bold md:text-4xl lg:text-5xl">Ready to Schedule Your Visit?</h2>
              <p className="max-w-2xl mb-8 text-lg opacity-90">
                Our team of specialists is ready to provide you with the care and support you deserve.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Button
                  size="lg"
                  variant="secondary"
                  className="gap-2 shadow-md bg-primary hover:bg-primary/90 text-white"
                  asChild
                >
                  <Link to="/book-appointment">
                    <Calendar className="w-5 h-5" />
                    Book an Appointment
                  </Link>
                </Button>
                <Button
                  size="lg"
                  variant="secondary"
                  className="gap-2 shadow-md bg-primary hover:bg-primary/90 text-white"
                  asChild
                >
                  <Link to="tel:+919966060000">
                    <Phone className="w-5 h-5" />
                    Call Us Now
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </section> */}
        </main>
      </div>
    </>
  )
}